import {
  type ClearRecallRequest,
  type ClearRecallResponse,
  type SetRecallRequest,
  type SetRecallResponse,
  type WorkspaceMembershipRole,
} from '@myplayer/schema';
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
  type Functions,
} from 'firebase/functions';

export interface FirebaseWebConfig {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly storageBucket?: string;
  readonly messagingSenderId?: string;
  readonly appId?: string;
  readonly functionsRegion?: string;
  readonly enableGoogleAuth?: boolean;
  readonly authEmulatorHost?: string;
  readonly firestoreEmulatorHost?: string;
  readonly functionsEmulatorHost?: string;
}

type EnvMap = Readonly<Record<string, string | boolean | undefined>>;

function getMetaEnv(): EnvMap | undefined {
  return (import.meta as { readonly env?: EnvMap }).env;
}

function readEnvString(key: string, fallback: string): string {
  const procEnv = (
    globalThis as { readonly process?: { readonly env?: Record<string, string | undefined> } }
  ).process?.env;
  const procVal = procEnv?.[key] ?? procEnv?.[key.replace(/^VITE_/, '')];
  if (typeof procVal === 'string' && procVal.trim().length > 0) return procVal.trim();
  const val = getMetaEnv()?.[key];
  if (typeof val === 'string' && val.trim().length > 0) return val.trim();
  return fallback;
}

export function isProductionBuild(): boolean {
  if (import.meta.env.PROD || import.meta.env.MODE === 'production') return true;
  return readEnvString('NODE_ENV', '') === 'production';
}

export function isLocalEmulatorMode(): boolean {
  if (import.meta.env.PROD) return false;
  if (isProductionBuild()) return false;
  return (
    readEnvString('VITE_FIREBASE_AUTH_EMULATOR_HOST', '').length > 0 ||
    readEnvString('VITE_FIRESTORE_EMULATOR_HOST', '').length > 0 ||
    readEnvString('VITE_FIREBASE_FUNCTIONS_EMULATOR_HOST', '').length > 0 ||
    readEnvString('MYPLAYER_ENV', '') === 'local' ||
    readEnvString('VITEST', '') === 'true'
  );
}

export function isGoogleAuthEnabled(): boolean {
  const raw = readEnvString('VITE_ENABLE_GOOGLE_AUTH', 'true').toLowerCase();
  return raw !== 'false' && raw !== '0';
}

export function loadFirebaseWebConfig(): FirebaseWebConfig {
  const localEmulator = !import.meta.env.PROD && isLocalEmulatorMode();
  const configuredApiKey = readEnvString('VITE_FIREBASE_API_KEY', '');
  if (!localEmulator) {
    if (configuredApiKey.length === 0) {
      throw new Error(
        'VITE_FIREBASE_API_KEY is required in production builds and when local emulator mode is not active.',
      );
    }
    if (configuredApiKey === 'demo-api-key') {
      throw new Error(
        'VITE_FIREBASE_API_KEY must not be "demo-api-key" outside local emulator mode.',
      );
    }
  }
  const apiKey = import.meta.env.PROD
    ? configuredApiKey
    : configuredApiKey.length > 0
      ? configuredApiKey
      : 'demo-api-key';
  const projectId = readEnvString(
    'VITE_FIREBASE_PROJECT_ID',
    !import.meta.env.PROD && localEmulator ? 'demo-mp-gs1-test' : '',
  );
  const authDomain = readEnvString('VITE_FIREBASE_AUTH_DOMAIN', `${projectId}.firebaseapp.com`);
  const storageBucket = readEnvString('VITE_FIREBASE_STORAGE_BUCKET', `${projectId}.appspot.com`);
  const messagingSenderId = readEnvString('VITE_FIREBASE_MESSAGING_SENDER_ID', '000000000000');
  const appId = readEnvString('VITE_FIREBASE_APP_ID', '1:000000000000:web:000000000000');
  const functionsRegion = readEnvString('VITE_FIREBASE_FUNCTIONS_REGION', 'europe-west2');
  const enableGoogleAuth = isGoogleAuthEnabled();
  const authEmulatorHost = localEmulator
    ? readEnvString('VITE_FIREBASE_AUTH_EMULATOR_HOST', '')
    : '';
  const firestoreEmulatorHost = localEmulator
    ? readEnvString('VITE_FIRESTORE_EMULATOR_HOST', '')
    : '';
  const functionsEmulatorHost = localEmulator
    ? readEnvString('VITE_FIREBASE_FUNCTIONS_EMULATOR_HOST', '')
    : '';
  return {
    apiKey,
    authDomain,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
    functionsRegion,
    enableGoogleAuth,
    ...(authEmulatorHost.length > 0 ? { authEmulatorHost } : {}),
    ...(firestoreEmulatorHost.length > 0 ? { firestoreEmulatorHost } : {}),
    ...(functionsEmulatorHost.length > 0 ? { functionsEmulatorHost } : {}),
  };
}

let cachedClients: {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly db: Firestore;
  readonly functions: Functions;
} | null = null;

export function resetFirebaseClientsForTests(): void {
  cachedClients = null;
}

function parseHostPort(hostPort: string, defaultPort: number): readonly [string, number] {
  const [rawHost, rawPort] = hostPort.split(':');
  const host = rawHost && rawHost.length > 0 ? rawHost : '127.0.0.1';
  const port = rawPort ? Number.parseInt(rawPort, 10) : defaultPort;
  return [host, Number.isNaN(port) ? defaultPort : port];
}

export function getFirebaseClients(): {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly db: Firestore;
  readonly functions: Functions;
} {
  if (cachedClients !== null) return cachedClients;

  const config = loadFirebaseWebConfig();
  const existing = getApps();
  const app =
    existing.length > 0 && existing[0] !== undefined
      ? existing[0]
      : initializeApp({
          apiKey: config.apiKey,
          authDomain: config.authDomain,
          projectId: config.projectId,
          ...(config.storageBucket !== undefined ? { storageBucket: config.storageBucket } : {}),
          ...(config.messagingSenderId !== undefined
            ? { messagingSenderId: config.messagingSenderId }
            : {}),
          ...(config.appId !== undefined ? { appId: config.appId } : {}),
        });

  const auth = getAuth(app);
  const db = getFirestore(app);
  const functions = getFunctions(app, config.functionsRegion ?? 'europe-west2');

  if (!import.meta.env.PROD && isLocalEmulatorMode()) {
    if (config.authEmulatorHost) {
      const authUrl = config.authEmulatorHost.startsWith('http')
        ? config.authEmulatorHost
        : `http://${config.authEmulatorHost}`;
      connectAuthEmulator(auth, authUrl, { disableWarnings: true });
    }
    if (config.firestoreEmulatorHost) {
      const [host, port] = parseHostPort(config.firestoreEmulatorHost, 8080);
      connectFirestoreEmulator(db, host, port);
    }
    if (config.functionsEmulatorHost) {
      const [host, port] = parseHostPort(config.functionsEmulatorHost, 5001);
      connectFunctionsEmulator(functions, host, port);
    }
  }

  cachedClients = { app, auth, db, functions };
  return cachedClients;
}

export type SetRecallCallablePayload = SetRecallRequest;
export type SetRecallCallableResult = SetRecallResponse;
export type ClearRecallCallablePayload = ClearRecallRequest;
export type ClearRecallCallableResult = ClearRecallResponse;

export function isFunctionsEmulatorOrLiveBackendConfigured(): boolean {
  if (import.meta.env.PROD || !isLocalEmulatorMode()) return true;
  const cfg = loadFirebaseWebConfig();
  return cfg.functionsEmulatorHost !== undefined && cfg.functionsEmulatorHost.length > 0;
}

async function invokeCallable<Req, Res>(name: string, payload: Req): Promise<Res> {
  const { functions } = getFirebaseClients();
  const res = await httpsCallable<Req, Res>(functions, name)(payload);
  return res.data;
}

export async function callSetRecallFunction(
  payload: SetRecallCallablePayload,
): Promise<SetRecallCallableResult> {
  if (
    !import.meta.env.PROD &&
    isLocalEmulatorMode() &&
    !isFunctionsEmulatorOrLiveBackendConfigured()
  ) {
    return {
      identifierKey: payload.identifierKey,
      version: 2,
      status: 'RECALLED',
      purgeId: `purge_recall_${payload.identifierKey}_v2`,
      activatedAt: new Date().toISOString(),
      auditId: `aud_recall_${payload.identifierKey}_v2`,
    };
  }
  return invokeCallable<SetRecallCallablePayload, SetRecallCallableResult>('setRecall', payload);
}

export async function callClearRecallFunction(
  payload: ClearRecallCallablePayload,
): Promise<ClearRecallCallableResult> {
  if (
    !import.meta.env.PROD &&
    isLocalEmulatorMode() &&
    !isFunctionsEmulatorOrLiveBackendConfigured()
  ) {
    return {
      identifierKey: payload.identifierKey,
      version: 3,
      status: 'ACTIVE',
      deactivatedAt: new Date().toISOString(),
      auditId: `aud_recall_off_${payload.identifierKey}_v3`,
    };
  }
  return invokeCallable<ClearRecallCallablePayload, ClearRecallCallableResult>(
    'clearRecall',
    payload,
  );
}

export interface ManageWorkspaceMembershipCallablePayload {
  readonly orgId: string;
  readonly email: string;
  readonly action: 'INVITE' | 'UPDATE_ROLES' | 'REVOKE';
  readonly orgRole?: 'ORG_ADMIN' | null;
  readonly workspaceRoles: Readonly<Record<string, WorkspaceMembershipRole>>;
}

export interface ManageWorkspaceMembershipCallableResult {
  readonly membershipId: string;
  readonly status: 'INVITED' | 'ACTIVE' | 'REVOKED';
  readonly updatedAt: string;
  readonly auditId?: string;
}

export function callManageWorkspaceMembershipFunction(
  payload: ManageWorkspaceMembershipCallablePayload,
): Promise<ManageWorkspaceMembershipCallableResult> {
  return invokeCallable('manageWorkspaceMembership', payload);
}

export type PartnerApiScope =
  | 'identifiers:read'
  | 'identifiers:write'
  | 'qualifiers:read'
  | 'qualifiers:write'
  | 'recalls:write'
  | 'recall:write'
  | 'domains:read'
  | 'domains:write'
  | 'analytics:read'
  | 'analytics:write'
  | 'webhooks:read'
  | 'webhooks:write'
  | 'webhooks:manage'
  | 'myplayer:sync';

export interface CreateApiClientCallablePayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly name: string;
  readonly scopes: readonly PartnerApiScope[];
  readonly rateLimitRpm: number;
  readonly expiresAt?: string | null;
}

export interface CreateApiClientCallableResult {
  readonly clientId: string;
  readonly keyPrefix: string;
  readonly rawApiKey: string;
  readonly createdAt: string;
}

export function callCreateApiClientFunction(
  payload: CreateApiClientCallablePayload,
): Promise<CreateApiClientCallableResult> {
  return invokeCallable('createApiClient', payload);
}

export interface RevokeApiClientCallablePayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly clientId: string;
}

export interface RevokeApiClientCallableResult {
  readonly clientId: string;
  readonly status: 'REVOKED';
  readonly revokedAt: string;
}

export function callRevokeApiClientFunction(
  payload: RevokeApiClientCallablePayload,
): Promise<RevokeApiClientCallableResult> {
  return invokeCallable('revokeApiClient', payload);
}

export interface VerifyCustomDomainCallablePayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly hostname: string;
}

export interface VerifyCustomDomainCallableResult {
  readonly hostname: string;
  readonly status:
    | 'PENDING_DNS'
    | 'PENDING_TXT'
    | 'VERIFIED'
    | 'PROVISIONING_TLS'
    | 'ACTIVE'
    | 'FAILING'
    | 'FAILED';
  readonly plainStatus?: 'Pending' | 'Verified' | 'Failed';
  readonly tlsStatus: 'PENDING' | 'ACTIVE' | 'RENEWING' | 'FAILED';
  readonly verifiedAt: string | null;
  readonly cnameTarget?: string;
  readonly txtHost?: string;
  readonly txtValue?: string;
  readonly verificationToken?: string;
  readonly acmeChallengeRecord?: {
    readonly name: string;
    readonly data: string;
  } | null;
  readonly failureReason?: string | null;
}

export type RequestCustomDomainCallablePayload = VerifyCustomDomainCallablePayload;
export type RequestCustomDomainCallableResult = VerifyCustomDomainCallableResult;

export function callRequestCustomDomainFunction(
  payload: RequestCustomDomainCallablePayload,
): Promise<RequestCustomDomainCallableResult> {
  return invokeCallable('requestCustomDomain', payload);
}

export function callVerifyCustomDomainFunction(
  payload: VerifyCustomDomainCallablePayload,
): Promise<VerifyCustomDomainCallableResult> {
  return invokeCallable('verifyCustomDomain', payload);
}

export {
  callArchiveWorkspaceFunction,
  callCancelScheduledChangeFunction,
  callCreateWorkspaceFunction,
  callGetWorkspaceScanAnalyticsFunction,
  callOffboardCustomDomainFunction,
  callRecheckCustomDomainFunction,
  callSetScheduledChangeFunction,
  callUpdateWorkspaceFunction,
  type ArchiveWorkspaceCallablePayload,
  type ArchiveWorkspaceCallableResult,
  type CancelScheduledChangeCallablePayload,
  type CancelScheduledChangeCallableResult,
  type CreateWorkspaceCallablePayload,
  type CreateWorkspaceCallableResult,
  type GetWorkspaceScanAnalyticsCallablePayload,
  type GetWorkspaceScanAnalyticsCallableResult,
  type OffboardCustomDomainCallablePayload,
  type OffboardCustomDomainCallableResult,
  type RecheckCustomDomainCallablePayload,
  type RecheckCustomDomainCallableResult,
  type SetScheduledChangeCallablePayload,
  type SetScheduledChangeCallableResult,
  type UpdateWorkspaceCallableFields,
  type UpdateWorkspaceCallablePayload,
  type UpdateWorkspaceCallableResult,
} from './firebase-callables.js';
export {
  callActivateInvitedMembershipFunction,
  callUpdateOrganisationFunction,
  type ActivateInvitedMembershipCallablePayload,
  type ActivateInvitedMembershipCallableResult,
  type UpdateOrganisationCallableFields,
  type UpdateOrganisationCallablePayload,
  type UpdateOrganisationCallableResult,
} from './organisation-firestore.js';
